import type { FC, PropsWithChildren, UIEvent } from 'react';
import { useCallback, useRef } from 'react';

interface NestedScrollViewProps {
  className?: string;
  threshold?: number;
  onScrollUp?: () => void; // invoked when scrolling up and within threshold of top
  onScrollDown?: () => void; // invoked when scrolling down past threshold
}

export const NestedScrollView: FC<PropsWithChildren<NestedScrollViewProps>> = ({
  children,
  className,
  threshold = 5,
  onScrollUp,
  onScrollDown
}) => {
  const lastScrollTopRef = useRef(0);

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const currentTop = target.scrollTop;
      const lastTop = lastScrollTopRef.current;
      const delta = currentTop - lastTop;
      lastScrollTopRef.current = currentTop;

      // Collapse on any meaningful downward scroll
      if (delta > threshold) {
        onScrollDown?.();
        return;
      }

      // Only expand when user scrolls up AND has reached (or is within threshold of) the top
      if (delta < -threshold && currentTop <= threshold) {
        onScrollUp?.();
      }
    },
    [onScrollDown, onScrollUp, threshold]
  );

  return (
    <div className={className} style={{ overflow: 'auto' }} onScroll={handleScroll}>
      {children}
    </div>
  );
};
